import { Component, Input, Output, EventEmitter } from "@angular/core";
import { DriverNote } from "@app/shared/models/drivers/DriverViewModel.model";

@Component({
  selector: "app-driver-note-cards",
  templateUrl: "./driver-note-cards.component.html",
  styleUrls: ["./driver-note-cards.component.css"],
})
export class DriverNoteCardsComponent {
  @Input() notes: DriverNote[] = [];
  @Input() searchText: string = "";
  @Input() canEdit: boolean = false;
  @Input() showDriverInfo: boolean = false;
  @Input() loading: boolean = false;
  @Input() pageSize: number = 10;

  @Output() editNote = new EventEmitter<DriverNote>();
  @Output() deleteNote = new EventEmitter<DriverNote>();
  @Output() viewNote = new EventEmitter<DriverNote>();

  // Pagination
  currentPage: number = 1;
  totalPages: number = 0;
  Math = Math;

  get paginatedNotes(): DriverNote[] {
    this.totalPages = Math.ceil(this.notes.length / this.pageSize);
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.notes.slice(startIndex, endIndex);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  nextPage() {
    this.goToPage(this.currentPage + 1);
  }

  previousPage() {
    this.goToPage(this.currentPage - 1);
  }

  resetPage() {
    this.currentPage = 1;
  }

  onCardClick(note: DriverNote) {
    this.viewNote.emit(note);
  }

  onEditClick(note: DriverNote, event: Event) {
    event.stopPropagation();
    this.editNote.emit(note);
  }

  onDeleteClick(note: DriverNote, event: Event) {
    event.stopPropagation();
    this.deleteNote.emit(note);
  }

  getHighlightedText(text: string): string {
    if (!this.searchText || !text) {
      return text || "";
    }
    const re = new RegExp(this.searchText, "gi");
    return text.replace(re, "<mark>$&</mark>");
  }

  isAccidentOrIncident(type?: string): boolean {
    return type === "Accident" || type === "Incident";
  }

  isDelayOrBreakdown(type?: string): boolean {
    return type === "Delays" || type === "Breakdown & follow up";
  }

  isDriverRelease(type?: string): boolean {
    return type === "Driver Release";
  }

  isFollowUpDueToday(note: DriverNote): boolean {
    if (!note.followUpDate) return false;
    const today = new Date();
    const followUp = new Date(note.followUpDate);
    return (
      today.getFullYear() === followUp.getFullYear() &&
      today.getMonth() === followUp.getMonth() &&
      today.getDate() === followUp.getDate()
    );
  }

  formatDate(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  formatTime(value?: string): string {
    if (!value) return '';
    // Try parsing as a full date first (e.g. ISO string)
    const asDate = new Date(value);
    if (!isNaN(asDate.getTime()) && value.includes('T')) {
      return asDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    // Try parsing HH:mm or HH:mm:ss
    const parts = value.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      if (!isNaN(hours) && !isNaN(minutes)) {
        const period = hours >= 12 ? 'PM' : 'AM';
        const h = hours % 12 || 12;
        return `${h}:${String(minutes).padStart(2, '0')} ${period}`;
      }
    }
    return value;
  }
}
